import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { TenantBranding } from "@/lib/branding.functions";

const TenantBrandingContext = createContext<TenantBranding | null>(null);

export function useTenantBranding() {
  return useContext(TenantBrandingContext);
}

/**
 * TenantThemeProvider — applies tenant branding as scoped CSS variables.
 * The dark theme of ZYNTRA is invariant; tenants only customize accents,
 * typography, radius and brand assets. Missing values fall back to the
 * platform tokens defined in src/styles.css.
 */
export function TenantThemeProvider({
  branding,
  children,
}: {
  branding: TenantBranding | null;
  children: ReactNode;
}) {
  const style = useMemo(() => {
    if (!branding) return undefined;
    const s: Record<string, string> = {};
    if (branding.primary_color) {
      s["--primary"] = branding.primary_color;
      s["--ring"] = branding.primary_color;
      s["--sidebar-primary"] = branding.primary_color;
    }
    if (branding.secondary_color) s["--secondary"] = branding.secondary_color;
    if (branding.accent_color) s["--accent"] = branding.accent_color;
    if (branding.border_radius) s["--radius"] = branding.border_radius;
    if (branding.font_family_body) {
      s["--font-sans"] = `${branding.font_family_body}, ui-sans-serif, system-ui, sans-serif`;
    }
    return s as React.CSSProperties;
  }, [branding]);

  return (
    <TenantBrandingContext.Provider value={branding}>
      <div data-tenant={branding?.tenant_slug ?? "zyntra"} style={style}>
        {children}
      </div>
    </TenantBrandingContext.Provider>
  );
}

/**
 * Small inline credit shown when a tenant chooses to keep the
 * "Powered by ZYNTRA" attribution.
 */
export function PoweredByZyntra({ className = "" }: { className?: string }) {
  const branding = useTenantBranding();
  if (!branding) return null;
  if (branding.tenant_slug === "zyntra") return null;
  if (!branding.show_powered_by_zyntra) return null;
  return (
    <p className={`text-xs text-muted-foreground ${className}`}>
      Powered by <span className="font-semibold gradient-text">ZYNTRA</span>
    </p>
  );
}
